import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  ArdoqGraphComponentForwardRefType,
  useGraphViewPerformanceTracking,
} from 'tabview/graphComponent/ArdoqGraphComponent';
import {
  canBypassLimit,
  isAboveItemLimit,
} from 'tabview/graphComponent/graphItemLimit';
import { canBypassViewDataLimit } from 'views/environmentals/viewDataLimits';
import { relationshipDiagramBypassDataLimit } from 'tabview/relationshipDiagrams/actions';
import { APIFieldAttributes, PersonalSetting, ViewIds } from '@ardoq/api-types';
import {
  Cursor,
  GraphComponent,
  GraphViewerInputMode,
  HierarchicalNestingPolicy,
  Matrix,
  Size,
} from '@ardoq/yfiles';
import { getViewModel$ } from './isometric/viewModel$';
import { CollapsibleGroupsYGraphBuilder } from './view/yFilesExtensions/CollapsibleGroupsYGraphBuilder';
import { isInScopeDiffMode, isLoadingScope } from 'scope/scopeDiff';
import { onLayoutGraph } from './view/yFilesExtensions/onLayoutGraph';
import { getLeftMenu as getLeftMenuBase, getRightMenu } from './menus';
import { configureLayout } from './view/configureLayout';
import { BlockDiagramVisualDiffSwitcher } from './view/BlockDiagramVisualDiffSwitcher';
import { IsometricNodeStyle } from './view/yFilesExtensions/isometric/IsometricNodeStyle';
import { SettingsBar, getFieldDropdownOptions } from '@ardoq/settings-bar';
import { type SettingsConfig, SettingsType } from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import {
  IsometricBlockDiagramViewProperties,
  IsometricBlockDiagramViewSettings,
} from './isometric/types';
import {
  getAddedAndUpdatedItems,
  xScaler,
  type RelationshipDiagramViewModel,
} from '@ardoq/graph';
import { IsometricGraphModelManager } from './view/yFilesExtensions/isometric/IsometricGraphModelManager';
import { WithLoadingIndicator } from 'tabview/WithLoadingIndicator';
import { dispatchAction } from '@ardoq/rxbeach';
import { notifyViewLoading } from 'tabview/actions';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { defaultLegendOnClick } from 'viewSettings/getRightMenuConfig';
import { returnZero } from '@ardoq/common-helpers';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { isPresentationMode } from 'env';
import { ArdoqGraphComponentWithLegend } from 'tabview/ArdoqGraphComponentWithLegend';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.ISOMETRIC_BLOCK_DIAGRAM;

const getLeftMenu = (
  args: Parameters<typeof getLeftMenuBase>[0] & {
    viewSettings: IsometricBlockDiagramViewSettings;
    numericFields: APIFieldAttributes[];
  }
): SettingsConfig[] => [
  ...getLeftMenuBase(args),
  {
    id: 'valueField',
    label: 'Select value field',
    iconName: IconName.ARROW_RIGHT_ALT,
    className: 'rotate-270',
    options: getFieldDropdownOptions({
      viewId: VIEW_ID,
      list: args.numericFields,
      settingsKey: 'selectedFieldNameValue',
      isActive: name => args.viewSettings.selectedFieldNameValue === name,
      onViewSettingsUpdate,
    }),
    type: SettingsType.DROPDOWN,
  },
];

const configureRendering = (graphComponent: GraphComponent) => {
  const inputMode = graphComponent.inputMode as GraphViewerInputMode;
  inputMode.navigationInputMode.enabled = false;
  inputMode.defaultCursor = Cursor.GRAB;
  graphComponent.maximumZoom = 1.6;
  graphComponent.minimumZoom = 0.01;
  graphComponent.mouseWheelZoomFactor = 1.1;
  graphComponent.projection = Matrix.ISOMETRIC;

  graphComponent.graphModelManager = new IsometricGraphModelManager();
  const { graphModelManager } = graphComponent;
  graphModelManager.hierarchicalNestingPolicy =
    HierarchicalNestingPolicy.GROUP_NODES;
  const {
    nodeLabelGroup,
    edgeLabelGroup,
    nodeGroup,
    edgeGroup,
    groupNodeGroup,
  } = graphModelManager;
  edgeLabelGroup.below(nodeGroup);
  nodeLabelGroup.below(edgeLabelGroup);
  edgeGroup.below(nodeLabelGroup);
  groupNodeGroup.below(edgeGroup);
};

const isEmptyGraph = (viewModel: RelationshipDiagramViewModel) => {
  const { groups, nodes, edges } = viewModel;

  return ![groups, nodes, edges].some(
    ({ add, update }) => add.length || update.length
  );
};

const ISOMETRIC_BLOCK_DIAGRAM_HOVER_MODE = {
  edgeHover: false,
  highlightAdjacent: true,
  transparentizeNonAdjacent: false,
};

const ViewContainer = styled.div`
  & .rotate-270 {
    transform: rotate(270deg);
  }
`;
const IsometricBlockDiagramView = (
  props: IsometricBlockDiagramViewProperties
) => {
  const ardoqGraphComponentRef =
    useRef<ArdoqGraphComponentForwardRefType>(null);
  const graphBuilder = useRef<CollapsibleGroupsYGraphBuilder | null>(null);
  const nodeStyle = useRef(new IsometricNodeStyle(returnZero));

  const setScaler = (
    selectedValueFieldDomain: [min: number, max: number],
    fieldValuesByModelId: Map<string, number | null>
  ) => {
    const scaler = xScaler([10, 50], selectedValueFieldDomain, [0, 1]);
    nodeStyle.current.scaler = graphNode => {
      const fieldValue = fieldValuesByModelId.get(graphNode.modelId) ?? null;
      if (typeof fieldValue !== 'number') return 10;
      return scaler(fieldValue) || 10;
    };
  };
  const {
    viewModel,
    viewInstanceId,
    viewSettings,
    scenarioName,
    numericFields,
    selectedValueFieldDomain,
    fieldValuesByModelId,
    clearEdges,
    errorInfoBoxLabels,
    i18nLocale,
    quickPerspectiveReferenceTypeFilters,
    legendProps,
    dataLimitExceeded,
    hasUserBypassedDataLimit,
  } = props;

  const onBypassDataLimit = useCallback(
    () =>
      dispatchAction(
        relationshipDiagramBypassDataLimit({ viewId: VIEW_ID }),
        viewInstanceId
      ),
    [viewInstanceId]
  );

  const buildGraph = useCallback(
    (
      graphComponent: GraphComponent,
      bypassLimit: boolean,
      clearEdges: boolean
    ) => {
      if (!graphBuilder.current) {
        graphBuilder.current = new CollapsibleGroupsYGraphBuilder(
          VIEW_ID,
          viewInstanceId,
          graphComponent.graph,
          () => nodeStyle.current,
          () => new Size(50, 50)
        );
      }

      if (clearEdges) {
        graphBuilder.current.clearEdges();
      }
      const groups = getAddedAndUpdatedItems(viewModel.groups);
      const nodes = getAddedAndUpdatedItems(viewModel.nodes);
      const edges = getAddedAndUpdatedItems(viewModel.edges);

      const graphItemLimitArgs = {
        viewId: VIEW_ID,
        nodeCount: nodes.length,
        edgeCount: edges.length,
        presentationMode: isPresentationMode(),
      };
      const aboveLimit = dataLimitExceeded
        ? true
        : isAboveItemLimit(graphItemLimitArgs);
      const canBypass = dataLimitExceeded
        ? canBypassViewDataLimit(VIEW_ID, dataLimitExceeded.itemCount)
        : canBypassLimit(graphItemLimitArgs);
      const isEmpty = !nodes.length && !edges.length && !groups.length;
      const hasLayoutUpdate = graphBuilder.current.hasLayoutUpdate({
        groups,
        nodes,
        edges,
      });
      if ((!bypassLimit && aboveLimit) || isEmpty) {
        graphBuilder.current.groupsSource = [];
        graphBuilder.current.nodesSource = [];
        graphBuilder.current.edgesSource = [];
        graphBuilder.current.clear();
      } else {
        graphBuilder.current.groupsSource = groups;
        graphBuilder.current.nodesSource = nodes;
        graphBuilder.current.edgesSource = edges;
        graphBuilder.current.updateGraph();
      }
      graphComponent.selection.clear();
      // graphComponent.projection = Matrix.ISOMETRIC;
      return { isAboveLimit: aboveLimit, isEmpty, canBypass, hasLayoutUpdate };
    },
    [viewModel, viewInstanceId, dataLimitExceeded]
  );

  const getContainer = () => containerRef.current;
  const getGraphComponent = () =>
    ardoqGraphComponentRef.current?.graphComponent ?? null;

  const {
    noConnectedComponents,
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
    referenceTypes,
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    expandedStateChangingGroupId,
    errors,
    hasClones,
  } = viewModel;
  useEffect(() => {
    if (noConnectedComponents) {
      // normally isBusy=false is dispatched in the graph layout callback, but in this case we have no graph, so we must dispatch isBusy=false
      setTimeout(() =>
        dispatchAction(
          notifyViewLoading(viewInstanceId, {
            isBusy: false,
          })
        )
      );
    }
  }, [viewInstanceId, noConnectedComponents]);

  const isLegendActive = viewSettings.isLegendActive ?? false;

  const { activeDiffMode } = viewSettings;

  setScaler(selectedValueFieldDomain, fieldValuesByModelId);
  const activeGroups = viewModel.groups.add.concat(viewModel.groups.update);

  const isEmptyView =
    !dataLimitExceeded && (isEmptyGraph(viewModel) || noConnectedComponents);

  const [clearedErrors, setClearedErrors] = useState(false);
  const [clearedHasClones, setClearedHasClones] = useState(false);
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_ISOMETRIC_BLOCK_DIAGRAM
  );

  const onLayoutGraphCallback = useMemo(
    () => onLayoutGraph(expandedStateChangingGroupId),
    [expandedStateChangingGroupId]
  );

  const { layoutOrientation, separateReferences } = viewSettings;
  const configureLayoutCallback = useCallback(
    (graphComponent: GraphComponent) =>
      configureLayout(graphComponent, viewModel, {
        layoutOrientation,
        separateReferences,
      }),
    [viewModel, layoutOrientation, separateReferences]
  );

  const buildGraphCallback = useCallback(
    (graphComponent: GraphComponent, bypassLimit: boolean) =>
      buildGraph(graphComponent, bypassLimit, clearEdges),
    [buildGraph, clearEdges]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <ViewContainer className={`tab-pane ${VIEW_ID}Tab active`}>
      <div className="menuContainer">
        <SettingsBar
          viewId={VIEW_ID}
          leftMenu={getLeftMenu({
            viewSettings,
            traversedOutgoingReferenceTypes,
            traversedIncomingReferenceTypes,
            referenceTypes,
            numericFields,
            viewId: VIEW_ID,
            viewInstanceId,
            onViewSettingsUpdate,
            activeGroups,
            isViewpointMode: false,
            quickPerspectiveReferenceTypeFilters,
            i18nLocale,
          })}
          rightMenu={getRightMenu({
            viewSettings,
            isViewpointMode: false,
            getContainer,
            getGraphComponent: () => getGraphComponent()?.current ?? null,
            viewId: VIEW_ID,
            legendOnClick: () => {
              defaultLegendOnClick({
                isLegendActive: !isLegendActive,
                viewId: VIEW_ID,
              });
            },
            onViewSettingsUpdate,
            isEmptyView,
            i18nLocale,
          })}
          i18nLocale={i18nLocale}
        />
      </div>
      <div style={{ flex: 1, position: 'relative' }} ref={containerRef}>
        <ArdoqGraphComponentWithLegend
          viewId={VIEW_ID}
          ref={ardoqGraphComponentRef}
          viewModel={viewModel}
          buildGraph={buildGraphCallback}
          enableStyles={true}
          configureLayout={configureLayoutCallback}
          configureRendering={configureRendering}
          onLayoutGraph={onLayoutGraphCallback}
          hoverMode={ISOMETRIC_BLOCK_DIAGRAM_HOVER_MODE}
          viewInstanceId={viewInstanceId}
          urlFieldValuesByComponentId={urlFieldValuesByComponentId}
          urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
          legendProps={legendProps}
          isLegendActive={isLegendActive}
          isViewpointMode={false}
          i18nLocale={i18nLocale}
          hasUserBypassedDataLimit={hasUserBypassedDataLimit}
          onBypassDataLimit={onBypassDataLimit}
        />
        <ErrorInfoBox
          errors={clearedErrors ? [] : errors}
          hasClones={!neverShowAgain && !clearedHasClones && hasClones}
          clearHasClones={() => setClearedHasClones(true)}
          clearErrors={() => setClearedErrors(true)}
          isShowNeverAgainSet={neverShowAgain}
          toggleNeverShowAgain={toggleNeverShowAgain}
          isPresentationMode={isPresentationMode()}
          {...errorInfoBoxLabels}
        />
        {(isInScopeDiffMode() || isLoadingScope()) && (
          <BlockDiagramVisualDiffSwitcher
            viewId={ViewIds.ISOMETRIC_BLOCK_DIAGRAM}
            scenarioName={scenarioName}
            activeDiffMode={activeDiffMode}
            i18nLocale={i18nLocale}
          />
        )}
      </div>
    </ViewContainer>
  );
};

const PerformanceTrackedIsometricBlockDiagram = (
  props: IsometricBlockDiagramViewProperties
) => {
  useGraphViewPerformanceTracking(props.viewModel, VIEW_ID);
  return <IsometricBlockDiagramView {...props} />;
};

const IsometricBlockDiagramViewWithLoadingIndicator = (
  props: IsometricBlockDiagramViewProperties
) => (
  <WithLoadingIndicator
    viewInstanceId={props.viewInstanceId}
    loadingSource="notifyViewLoading"
    showContentWhileLoading={false}
    i18nLocale={props.i18nLocale}
  >
    <PerformanceTrackedIsometricBlockDiagram {...props} />
  </WithLoadingIndicator>
);

export const ConnectedIsometricBlockDiagramView = connectInstanceWithProps(
  IsometricBlockDiagramViewWithLoadingIndicator,
  getViewModel$
);
